<script>
  import Highlight from "svelte-highlight";
  import { HighlightSvelte } from "svelte-highlight";
  import javascript from "svelte-highlight/src/languages/javascript"
  import swift from "svelte-highlight/src/languages/swift"
  import xcode from "svelte-highlight/src/styles/xcode";
  import {push} from "../EndpointsAndTransforms";
  import {createEventDispatcher} from "svelte";

  let editor

  const dispatch = createEventDispatcher()
  function goBack() {
    dispatch('back')
  }

  const engineerModel = 'struct Engineer: Codable {\n' +
    '    let name: String\n' +
    '    let favoriteLanguages: Array<Languages>?\n' +
    '    let attendedDubDub: Array<DubDubYear>?\n' +
    '    \n' +
    '    enum Languages: String, Codable {\n' +
    '        case swift, javascript, ruby, java, haskell, elm, rust\n' +
    '    }\n' +
    '    \n' +
    '    typealias DubDubYear = Int\n' +
    '}'

  const inboundModel = 'struct Inbound: Decodable {\n' +
    '    struct Meta: Decodable {\n' +
    '        //This is the workaround for JSON and some clients not supporting discriminated unions. Yes that means if you write a new \'endpoint\' you have to write it\'s name twice: once here, and again in the Body enum\n' +
    '        enum RequestTypes: String, Decodable { case createNewEngineer, fetchEngineersSocialNetwork }\n' +
    '        let requestType: RequestTypes // Sent by client as a request header mapped in APIGateway\n' +
    '    }\n' +
    '    \n' +
    '    enum Body { //When a client makes a POST request to our API, we\'ll try to decode the httpBody of the request as the associated type\n' +
    '        case createNewEngineer(Engineer)\n' +
    '        case fetchEngineersSocialNetwork(EngineerUUID)\n' +
    '    }\n' +
    '    \n' +
    '    let meta: Meta\n' +
    '    let body: Body\n' +
    '    \n' +
    '    enum CodingKeys: String, CodingKey { case meta, body }\n' +
    '    enum DecodingError: Error { case badMetaData, badPayload }\n' +
    '    \n' +
    '    init(from decoder: Decoder) throws {\n' +
    '        let container = try decoder.container(keyedBy: CodingKeys.self)\n' +
    '        guard let decodedMeta = try? container.decode(Meta.self, forKey: .meta) else { throw DecodingError.badMetaData }\n' +
    '            self.meta = decodedMeta\n' +
    '        \n' +
    '        switch decodedMeta.requestType {\n' +
    '        case .createNewEngineer:\n' +
    '            guard let payload = try? container.decode(Engineer.self, forKey: .body) else { throw DecodingError.badPayload }\n' +
    '            self.body = .createNewEngineer(payload)\n' +
    '        case .fetchEngineersSocialNetwork:\n' +
    '            guard let payload = try? container.decode(EngineerUUID.self, forKey: .body) else { throw DecodingError.badPayload }\n' +
    '            self.body = .fetchEngineersSocialNetwork(payload)\n' +
    '        }\n' +
    '    }\n' +
    '}'

  const handlerShell = 'struct Handler: EventLoopLambdaHandler {\n' +
    '    typealias In = Inbound\n' +
    '    typealias Out = LandingAPIResponse\n' +
    '    \n' +
    '    init(context: Lambda.InitializationContext) {\n' +
    '        //When the Lambda first spools up, we get the chance to configure stuff here\n' +
    '        //I use this method to setup Soto with my AWS credentials, and configure Soto.DynamoDB, Soto.S3, Soto.Neptune etc...\n' +
    '    }\n' +
    '    \n' +
    '    func handle(context: Lambda.Context, event: Inbound) -> EventLoopFuture<LandingAPIResponse> {\n' +
    '        switch event.body { //Yay! We get to switch off the enum in our Inbound model here\n' +
    '        case .createNewEngineer(let engineer): return //we need to return an EventLoopFuture<LandingAPIResponse> here\n' +
    '        case .fetchEngineersSocialNetwork(let engineerUUID):  return //and another one here\n' +
    '        }\n' +
    '    }\n' +
    '}'

  const handlerDetail = 'struct Handler: EventLoopLambdaHandler {\n' +
    '    typealias In = Inbound\n' +
    '    typealias Out = LandingAPIResponse\n' +
    '    init(context: Lambda.InitializationContext) {/* initialization work */}\n' +
    '    \n' +
    '    func handle(context: Lambda.Context, event: Inbound) -> EventLoopFuture<LandingAPIResponse> {\n' +
    '        switch event.body {\n' +
    '        case .createNewEngineer(let engineer): return handleCreate(engineer, context)\n' +
    '        case .fetchEngineersSocialNetwork(let engineerUUID):  return handleFetch(engineerUUID, context)\n' +
    '        }\n' +
    '    }\n' +
    '    \n' +
    '    //I like to break each of these out into stand-alone functions, but you could have done this work inside the switch statement, or organized it any way you like (provided it compiles...)\n' +
    '    func handleCreate(_ engineer: Engineer, _ context: Lambda.Context) -> EventLoopFuture<LandingAPIResponse> {\n' +
    '        //sanitize the inputs, use Soto to write stuff down in the AWS database of your choosing\n' +
    '        //do any needed business logic, throw errors\n' +
    '        //...and finally if we succeed\n' +
    '        return context.eventLoop.makeSucceededFuture("Encodable response object goes here")\n' +
    '    }\n' +
    '    func handleFetch(_ uuid: EngineerUUID, _ context: Lambda.Context) -> EventLoopFuture<LandingAPIResponse> {\n' +
    '        //similar to the create method, do your backend work here, facilitated by Soto\'s tooling.\n' +
    '    }\n' +
    '}'

  const lambdaRun = 'Lambda.run { Handler(context: $0) }'

  const fetchRequest = '  function apiRequest(body, method) {\n' +
    '    let requestBody = JSON.stringify(body);\n' +
    '    let endpoint = \'https://subdomain.domain.com\'\n' +
    '\n' +
    '    return fetch ( endpoint,\n' +
    '      {\n' +
    '        method: \'POST\',\n' +
    '        mode: \'cors\',\n' +
    '        credentials: \'omit\',\n' +
    '        headers: {\n' +
    '          \'Content-Type\': \'application/json\',\n' +
    '          \'Request-Type\': method,\n' +
    '        },\n' +
    '        body: requestBody\n' +
    '      }\n' +
    '    ).then(r => r.json())\n' +
    '  }'

  const fetchComponent = '< script>\n' +
    '  function apiRequest(body, method) { ... }\n' +
    '\n' +
    '  let engineerName;\n' +
    '  function submitNewEngineerName() {\n' +
    '    apiRequest({\'name\': name}, \'createNewEngineer\')\n' +
    '  }\n' +
    '< /script>;\n'+
    '\n'+
    '<form on:submit|preventDefault={submitNewEngineerName}>\n'+
    '  <p>What\'s Your Name, Man???</p>\n'+
    '  <input bind:value={engineerName} placeholder="Alexander Hamilton">\n'+
    '  <button type="submit">Submit</button>\n'+
    '</form>'

</script>

<svelte:head>
  {@html xcode}
</svelte:head>

<main>
  <goBackArea on:click="{() => {
    push('rants')
    goBack()
  }}">
    <p><i>back to list</i></p>
  </goBackArea>

  <h2>
    Serverless full-stack development for the Mobile minded engineer
  </h2>
  <p><img class='drop' src="/images/sled-dog cat.png"/><i>Day 45: and the AWS billing
    department still doesn't know that I only really speak Swift and ObjC. Or perhaps they don't
    care, as long as the monthly $$ charge goes through...</i></p>

  <h5>Do you even full-stack?</h5>
  <p>Ocassionally, I would invite a web or backend engineer to help us interview new iOS
    engineering candidates.</p>
  <p>Sure, having them help our iOS team do an interview built better cross-functional team
    bonds and a stronger overall engineering organization, but more importantly they weren't
    myopically focused on the details and minutiae of our iOS specific frameworks and tooling.
    And often times they'd see things we missed.</p>
  <p>For example; they wouldn't ask candidates to compare/contrast protocol witness tables and
    runtime dynamism against static types, generics, and the <code>@inlinable</code> attribute.
    Instead they'd ask more simple questions to find out what a candidate thought about making
    tradeoffs in computational workload between compile-time & runtime, or between client &
    server.</p>
  <p>But it also had the effect of showing the web &/or backend engineer the way that us mobile
    folks thought about our apps, and how expansive we believed our code & tooling to really be.</p>
  <p>Frequently in the post-interview conversation among our team, the web or backend engineer
    would ask us something like,</p>
  <p>"so... do... do you all see yourselves as 'full-stack' engineers... just because
    iOS has local disk storage???"</p>

  <p><img src="/images/codereview.jpg"><i>(Me imagining myself as every engineer on a
    cross-functional dev team, absolutely glowing about how good my PR is)</i></p>

  <h5>"What's in a stack? That which we call an 'app' in any other language/execution context would
    smell just as sweetly of tech-debt dumpster fire a dozen releases later anyway..."</h5>
  <p><i>--Shakespeare, probably.</i></p>

  <p>Looking back, I think I squandered those 'full-stack' discussions with my non-iOS
    engineering friends. Each of us would be too invested in our professional and personal
    identity in our respective tech stack / niches to truly engage.</p>
  <p>And despite years of tinkering with other disciplines on nights and weekends, I never got
    to a place where I could do anything useful with React or Node or Rails or R, etc...</p>
  <p><leftFloat><img src="/images/venn.png"/></leftFloat>I was stuck in the trap of narrow specialization/mastery in iOS, which leads to higher
    pay/title/job-security, but also makes truly taking something new on seem not worth it.</p>
  <p>I know some folks who have been able to successfully learn multiple disciplines over the span
    of their software engineering careers. But for me, it took more poignant circumstances from
    the world and more dramatic actions from me to finally break out of my iOS only niche.</p>
  <p>In the summer of 2020, just shy of the '10 years in iOS' mark, with the whole family stuck at
    home for the pandemic, I bought a new collection of IDE's from JetBrains and quit my job.</p>
  <p><img src="/images/Bierstadt.jpeg"/><i>Several days after quitting my job, and 9am finds me on
    the trail above tree-line with my older kids, instead of triple booked for meetings and updating
    Jira...</i></p>

  <h5>Frankensteins' Full-Stack Monster</h5>
  <p>So what kind of monstrosity does an iOS engineer stitch together when they've traded their
    paycheck for an AWS bill?</p>
  <p>I'm glad you asked! And I'm happy to show you!</p>

  <fullWidth>
    <img src="/images/StackDiagram.png"/>
  </fullWidth>

  <h6>clients:</h6>
  <ul>
    <p>Web: Svelte & JavaScript, deployed via AWS Amplify with true CD hooks through GitHub
      Actions</p>
    <p>iOS: Swift, SwiftUI</p>
    <p>Android: Kotlin, Jetpack Compose (my weakest skill set right now...)</p>
  </ul>

  <h6>backend:</h6>
  <ul>
    <p>Server-Side Swift, cross-compiler toolchain (no Docker required!), deployed as AWS Lambda
      functions</p>
  </ul>

  <h6>data</h6>
  <ul>
    <p>AWS: DynamoDB, S3, Neptune</p>
  </ul>

  <h6>infrastructure</h6>
  <ul>
    <p>AWS: CloudWatch, Route 53, APIGateway, CloudFront, Amplify, KeyManagementServices, Lambda,
      Cognito, WorkMail</p>
  </ul>

  <h5>Where the magic happens</h5>
  <p>This post would get unreasonably long if I talked through the code and configurations of
    the whole stack. So I'll have to do several more separate in-depth posts on getting all those
    AWS components configured and able to talk to each other, but we'll skip straight to the
    part I've had the most fun with, and what feels the most powerful to me.</p>
  <p><img src="/images/TechDebt.png"/> <strong>"Tech Debt is at its' costliest at the Model
    Layer"</strong></p>
  <p>I agree with Renaud, and offer my own corollary to it: My thinking is the most powerful and
    rewarding at the Model Layer too!</p>
  <p>To maximize that model layer power, when I'm adding some new functionality I start by
    focusing my mind on the models that will be passed back and forth between clients and the
    backend.</p>
  <p>Under this worldview, the client is just a tool to render my models into human
    readable/interactive forms. And the backend is just there to read/write from data-stores and
  apply business logic.</p>
  <p>Of course there are meaningful implementation details of languages, frameworks and tooling
    in the clients and the backend that will be impacted by my modeling decisions; but I try as
  hard as I can to be okay deferring those details for later. If I can cleanly & cleverly model
    the data and interactions of my distributed system first, then I've already set myself up
    for success when I have to tackle the client and backend implementation details.</p>

  <h5>Contrived example, and some Code!</h5>
  <p>Let me show you what this idea looks like with a contrived example, that I've stripped down
  for clarity to focus on the core mechanisms.</p>
  <p>We'll start with by modeling something, then show the glue-code to 'deploy' that model in
  a Lambda; and finally, we'll hint at what the implementation details would look like to
    consume this new model in both the backend, and a web client.</p>
  <p>Suppose I wanted to collect some info about engineers using my app, and use it to connect
    them with each-other based on shared languages, or conferences they've both attended.</p>
  <p>Perhaps I decide to model with this (contrived, slimmed down for clarity) struct:</p>
  <codeContainer>
    <Highlight language={swift} code={engineerModel}/>
  </codeContainer>
  <p>Great! It doesn't matter if the client is Web, iPhone, AppleTV, or an animatronic robot
    using 'Hey Google' (sorry Siri...); We can look at this model and imagine how any client can
  collect this information, stuff it into a JSON representation, and throw it over the internet
    to our endpoint.</p>
  <p>Similarly, we can imagine that a backend is going to have to do things like sanitize
    inputs, check for already existing duplicate records, write down new info, create new
    relationships between existing engineers who speak the same language, etc...</p>
  <p>But we'll ignore those for now, and finish the glue code to 'ship' this new model in our
    serverless cloud stack.</p>
  <codeContainer>
    <Highlight language={swift} code={inboundModel}/>
  </codeContainer>
  <p>Our Lambda will be spooled up (or just invoked if one is already running) in response to a
    client's POST request flowing through AWS APIGateway. We're taking advantage of methods in
    Apple's AWSLambdaRuntime framework which do the magic you'd expect from the <code>Codable
    </code> protocol .</p>
  <p>As we add new requestTypes later, swiftc will tell us if our Inbound model
    will work just by verify the Decodable conformance</p>
  <p>That let's us write/maintain a single `Inbound` model which is as extensible as the enums
    inside of it are (ie. very!).</p>
  <p>Instead of having multiple routes/endpoints/paths for multiple services, I have something
    wonderful that serverless and Swift lets us do. Something akin to the magic of GraphQL.
  </p>
  <p>Want a new route/endpoint? Add a new case to the Body. The case name is your new
    `requestType` that client's will send up as an httpHeader, and the associated type is the
    model you want to pass over the network.</p>
  <p>The <code>RequestTypes</code> enum on line 4 is an affordance we make for JSON (and some
    client
    languages) not supporting discriminated unions (tagged union, sum type, etc...). You can see
  where we decode it on line 21, then switch off it on line 24 to allow us to decode the request
    body as the right kind of type for that request. Using an alternate serialization protocol
    that supported sum types would avoid this, and would make you look pretty cool!
  </p>
  <p>The other bit of tedium / glue-code to notice here is the init(from decoder:) method, where
  we mechanically make sure that JSONDecoder() tries to decode the right types for each case. Of
  course you could also do fancy decoding footwork here too if you needed it.</p>

  <h5>Consuming `Engineer` on the backend</h5>
  <p><leftFloat><img src="/images/runLambda.png"/></leftFloat>I'm using Apple's AWSLambdaRuntime.
    While there are some awesome methods available that let you pass a straightforward closure,
    which is really useful when you first start playing with Swift Lambdas, I'm using the more
    complex EventLoopLambdaHandler api.</p>
  <p>With the EventLoopLambdaHandler based api, I get the chance to put SwiftNIO to work, and
  most importantly get to use Promises. Super useful as my lambda code will be calling many
    other AWS services and processing their responses.</p>
  <p>(Actors note:) Now that Actors & Async/Await have shipped in Swift, I'll be looking into
    switching my tooling over in the future.
  </p>
  <p>Here's the shell of what we need to write into our EventLoopLambdaHandler conforming type:</p>
  <codeContainer>
    <Highlight language={swift} code={handlerShell}/>
  </codeContainer>
  <p>Use Decodable/Encodable types for the In/Out typealias' respectively, and the framework
    will do all the boring serialization work for you. Inbound requests which successfully
    decode are passed to your `handle` method. And returning an EventLoopFuture sends your
    response back to the calling client.</p>
  <p>I've been impressed overall with the api design of AWSLambdaRuntime. For me it's been
    delightful to use.</p>
  <p>Since the `body` property of our `Inbound` model is an enum, we can just switch off the
    request type, and get busy doing backend work with databases and logic!</p>
  <p>Organize the code according to your tastes, but here's what it looks like if you break each
  requestType handler into its' own method and forward the data & context over inside the switch</p>
  <codeContainer>
    <Highlight language={swift} code={handlerDetail}/>
  </codeContainer>
  <p>And for the final bit of code to stitch our Swift together as an executable that can run on
    AmazonLinux2, we toss this single line into our <code>main.swift</code> file:</p>
  <codeContainer>
    <Highlight language={swift} code={lambdaRun}/>
  </codeContainer>
  <p>At this point, you can actually just run the Lambda from Xcode locally, and use curl to
    test it out. Use the AWS web console to verify that new DB records were
    added / modified / whatever your code does.</p>

  <h5>Ship the backend</h5>
  <p>Assuming we're happy with how the Lambda behaved when we called it from curl, we just need
    to compile it for the right chip architecture/OS, and upload it to AWS.</p>
  <p>The official Apple AWSLambdaRuntime project details how to use Docker to compile our
    executable for AWS. I'm sure that works, and has its' advantages, but I prefer to just let
    swift cross-compile things directly by
    <a href="https://github.com/SwiftXcode/swift-lambda">using this awesome tool.</a></p>
  <p>Deliver the .zip file to AWS Lamda via your chosen technique (swift-lambda has a `deploy`
    command, or you can bash the AWS CLI to upload the .zip to S3 and then update the Lambda, or
  you can just use the AWS web console and drag the .zip with Finder...)</p>
  <p>In my experience, it takes all of about 3 seconds for the new code to be live after it
    finishes uploading.</p>
  <p>That's... significantly faster than my past 10 years of submitting binaries to
    AppStoreConnect...</p>

  <h5>Let's put it on the internet!</h5>
  <p>When I first quit my job back in 2020, I picked up React again to build web, since I had
    some previous experience with it, and am generally a fan of Elm-like FRP tooling for UI.</p>
  <p>I had just barely finished learning React Hooks (new since I'd last used React, and pretty
    cool!), when I came across <a href="https://svelte.dev">Svelte.dev</a>.</p>
  <p>I've really enjoyed learning/using Svelte! As a Mobile engineer who didn't speak that
    much javascript, I have to say it's been really approachable, and I've been able to build most
    anything I've wanted to build with it.</p>
  <p>So let's look at some code to see what it's like.</p>
  <p>First we need to be able to call our endpoint, and return a Promise</p>
  <codeContainer>
    <Highlight language={javascript} code={fetchRequest}/>
  </codeContainer>
  <p>And then here's a bare-bones Svelte component to let you type in a name and fire it off to our
    lambda:</p>
  <codeContainer>
    <HighlightSvelte code={fetchComponent}/>
  </codeContainer>
  <p>You may need to disable CORS in the dev tools of your browser, but otherwise this
    bare-bones web component should also successfully invoke your lambda and write to your DB's</p>


  <h5>Continuous Deployment and my 'Set & Forget' AWS configuration</h5>
  <p>I've worked in shops where new engineers pushed a commit to production before lunch on the
    first day, and in shops where 200 person teams shipping a new feature in 18 months was
    considered 'lightning fast for our company!'</p>
  <p> **Spoiler Alert** Companies with that first kind of dev shop have a chance in the future,
    and can ship actual technology that real people will pay money for. Companies with the second
    kind of shop had better pay their lobbyists well to make sure they have unfair regulatory
    capture, or some other market lock-in or quasi-monopoly advantage, because they are doomed as
    a technology company.</p>
  <p>I'm not saying that my setup could work as-is for a large engineering
    organization; however it's supremely important to me that my CI/CD experience be nothing less
    than magical for me as a solo developer.</p>
  <p>If it wasn't magic for just me, then I'd have real problems scaling it to a bigger team.</p>

  <p>But our industry has made fantastic progress with CI/CD tooling and techniques, so there's
    nothing but good news to be found here.</p>
  <p>On top of the tooling available these days, I've also structured my stack to minimize the
    number of brittle, slow, or finicky things that need to change, and maximize my time spent
  focused on only the relevant code.</p>
  <p>Here's the biggest keys:</p>
  <ol>
    <p>AWS will let me trade $$ for effectively infinite on-demand instances of my Lambdas</p>
    <p>Also in exchange for $$, I can configure all of my infrastructure in such a way that
      completely new full-stack features can be automatically deployed simply by landing a PR in
    my mono-repo.</p>
    <p>Github Actions and the AWS CLI are powerful enough to help me automate special steps with
    some configuration and some bash for the weird stuff I want to paper over.</p>
  </ol>

  <p>Between clicking merge, and being live in prod, I barely have time** to get a new cup of
    coffee or tea (**offer not valid on Apple AppStore...)</p>
  <p>Let's consider a hypothetical PR where I've introduced a new model like we did with
    `Engineer` earlier.</p>
  <p>It's a mono-repo, so the PR has a few files changed in my backend Swift Lambda, a few new
    files in my Svelte Web client, and similar changes to a few files in the iOS and Android
    client folders too.</p>
  <p>Github Actions have helped me to gate the PR's ability to be merged by building the various
  apps, getting green runs from the test suites, and uploading the new Lambda to S3</p>
  <p>I still manually merge the PR, so I'm in control of when the deployment happens.</p>
  <p>As soon as the PR merges, the Lambda is updated with the new executable, and AWS Amplify
    automatically notices the new commit and deploys my updated web client within a minute or
    two.</p>
  <p>PlayStore and AppStoreConnect are painfully slower, but since I support versioning with my
    Lambda, the existing apps on phones will continue to call my Lambda successfully. And as
    soon as the new apps do go live on their respective stores, the Lambda is already live and
    ready for them.</p>
</main>

<style>
  main {
    display: flex;
    flex-direction: column;
    flex-width: nowrap;
    justify-content: flex-start;
    overflow-y: scroll;
    scrollbar-width:none;

    text-align: left;
    max-width: 800px;
    margin-right: auto; margin-left: auto;
    padding-left: 1rem; padding-right: 1rem;
  }
  main::-webkit-scrollbar {
    display: none;
  }
  codeContainer {
    max-width: 95%;
    margin: 1rem;
    border: solid 1px black;
    font-size: 1.5rem;
  }
  h5 {
    margin-bottom: 0px;
  }
  h6 {
    margin-bottom: 0px; margin-top: 10px;
  }
  p {
    text-align: left;
    max-width: 42rem;
    padding: 1rem;
    /*margin: 0 0 0 1rem;*/
    margin: 0;
    line-height: 2rem;
    font-size: 22px;
    font-weight: 400;
  }
  strong {
    font-weight: 700;
  }
  ul p {
    padding: 3px;
  }
  img {
    max-width: 350px;
    height: auto;
    float: right;
    margin: 4px;
    padding-left: 10px; padding-right: 10px;
    -webkit-filter: drop-shadow(0px 5px 5px #272d35);
    filter: drop-shadow(0px 5px 5px #272d35);
  }
  leftFloat img {
    float: left;
  }

  fullWidth img {
    max-width: 97%;
    height: auto;
    padding-left: 10px; padding-right: 10px;
    -webkit-filter: drop-shadow(0px 5px 5px #272d35);
    filter: drop-shadow(0px 5px 5px #272d35);
  }

  goBackArea {
    max-width: 10rem;
    margin-top: 1rem;
  }
  goBackArea:hover {
    box-shadow: -2px 9px 10px -4px #272d35, 0px -9px 10px -4px #272d35;
    border-radius: 8px;
    background-color: rgb(248,248,248);
  }

  code {
    background-color: whitesmoke;
  }

  @media only screen and (max-width: 600px) {
    h2 {
      font-size: 2.0rem;
    }
    p {
      max-width: 95%;
    }
    fullWidth img {
      max-width: 92%;
    }
    main {
      overflow-x: hidden;
      max-width: 97vw;
    }

  }
</style>